import {
  BrowserRouter as PublicRouter,
  HashRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { useEffect } from "react";
import { useRef } from "react";
import LoginForm from "./components/LoginForm";
import RegisterForm from "./components/RegisterForm";
import OwnerDashboard from "./pages/member/OwnerDashboard";
import MyTests from "./pages/member/MyTests";
// import SalesReports from "./pages/owner/SalesReports";
// import OwnerProfile from "./pages/owner/Profile";
import GameIndex from "./pages/user/Index";
import Learn from "./pages/user/Learn";
import Donate from "./pages/user/Donate";
import History from "./pages/user/History";
import Championship from "./pages/user/Championship";
import Stats from "./pages/user/Stats";
import Ranking from "./pages/user/Ranking";
import Store from "./pages/user/Store";
import Premium from "./pages/user/Premium";
import Friends from "./pages/user/Friends";
import FriendProfile from "./pages/user/FriendProfile";
import Tournament from "./pages/user/Tournament";
import Settings from "./pages/user/Settings";
import SpectateMatches from "./pages/user/SpectateMatches";
import Lobby from "./pages/user/Lobby";
import CreateGame from "./pages/user/CreateGame";
import PlayTournaments from "./pages/user/PlayTournaments";
import PlayBot from "./pages/user/PlayBot";
import PlaySandbox from "./pages/user/PlaySandbox";
import PlayLocalFriends from "./pages/user/PlayLocalFriends";
import DesignMode from "./pages/user/DesignMode";
import LandingPage from "./pages/LandingPage";
import GuestLaunch from "./pages/GuestLaunch";
// import TesterProfile from "./pages/tester/Profile";
// import YourCourses from "./pages/tester/YourCourses";
import AdminDashboard from "./pages/admin/AdminDashboard";
import ProtectedRoute from "./components/ProtectedRoute";
import PublicRoute from "./components/PublicRoute";
import { AuthProvider } from "./services/AuthContext"; // Wrap the app with AuthProvider
import { useAuth } from "./services/AuthContext";
import { notifyPageVisit } from "./services/visitNotifications";
import OwnerLayout from "./layouts/OwnerLayout";
import GameLayout from "./layouts/GameLayout";
import AdminLayout from "./layouts/AdminLayout";
import { isPublicLearnPathname, PUBLIC_LEARN_BASE } from "./utils/learnRoutes";
import GuestLinkEntry from "./pages/GuestLinkEntry";
import { SHORT_LINK_PATH_PATTERN } from "./utils/guestLinkAccess";

// La pantalla de partida se monta de nuevo al pasar a otra partida (Volver a jugar, Revancha).
const TournamentRoute = () => {
  const { id } = useParams();
  return <Tournament key={id} />;
};

// Short share links (juegoxima.com/p/ID, /t/ID, /i/ID) are real paths; turn them into
// the hash route the app uses before the router reads the URL.
const rewriteShortShareLink = () => {
  if (typeof window === "undefined") return;
  const match = SHORT_LINK_PATH_PATTERN.exec(String(window.location.pathname || ""));
  if (!match) return;
  const [, kind, id] = match;
  window.history.replaceState(null, "", `/#/entrar/${kind.toLowerCase()}/${id.toLowerCase()}`);
};
rewriteShortShareLink();

// Same short links written as hash routes (juegoxima.com/#/p/ID).
const ShortLinkRedirect = ({ kind }) => {
  const { id = "" } = useParams();
  return <Navigate to={`/entrar/${kind}/${String(id).toLowerCase()}`} replace />;
};

const OWNER_ROLES = ["member", "owner", "manager"];
const PHONE_WIDTH_QUERY = "(max-width: 820px)";
const TOUCH_POINTER_QUERY = "(pointer: coarse)";
const DEFAULT_VIEWPORT_META_CONTENT = "width=device-width, initial-scale=1";
const PHONE_LANDSCAPE_DESKTOP_VIEWPORT_WIDTH = 1920;
const PHONE_LANDSCAPE_DESKTOP_VIEWPORT_HEIGHT = 945;

const clampNumber = (value, min, max) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return min;
  return Math.min(Math.max(numeric, min), max);
};

const isPhoneMobileDevice = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  const userAgent = String(window.navigator.userAgent || "");
  const uaDataMobile = window.navigator.userAgentData?.mobile === true;
  const looksLikeTablet = /iPad|Tablet|Kindle|Silk|SM-T|Nexus 7|Nexus 10/i.test(
    userAgent
  );
  const looksLikePhoneAgent =
    /iPhone|iPod|Android.+Mobile|Windows Phone|webOS|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(
      userAgent
    );
  const phoneSizedViewport =
    typeof window.matchMedia === "function"
      ? window.matchMedia(PHONE_WIDTH_QUERY).matches
      : Number(window.innerWidth) <= 820;
  const coarsePointer =
    typeof window.matchMedia === "function" &&
    window.matchMedia(TOUCH_POINTER_QUERY).matches;

  return !looksLikeTablet && (looksLikePhoneAgent || uaDataMobile || (phoneSizedViewport && coarsePointer));
};

const isPhonePortraitMobileDevice = () => {
  if (typeof window === "undefined") return false;
  if (!isPhoneMobileDevice()) return false;
  if (typeof window.matchMedia === "function") {
    return window.matchMedia("(orientation: portrait)").matches;
  }
  return Number(window.innerHeight) >= Number(window.innerWidth);
};

const isPhoneLandscapeMobileDevice = () => {
  if (typeof window === "undefined") return false;
  if (!isPhoneMobileDevice()) return false;
  if (typeof window.matchMedia === "function") {
    return window.matchMedia("(orientation: landscape)").matches;
  }
  return Number(window.innerWidth) > Number(window.innerHeight);
};

const resolvePhoneLandscapeDesktopScale = () => {
  if (typeof window === "undefined") return 1;
  const screenWidth =
    Number(window.screen?.width) ||
    Number(window.screen?.availWidth) ||
    Number(window.innerWidth) ||
    PHONE_LANDSCAPE_DESKTOP_VIEWPORT_WIDTH;
  const screenHeight =
    Number(window.screen?.height) ||
    Number(window.screen?.availHeight) ||
    Number(window.innerHeight) ||
    PHONE_LANDSCAPE_DESKTOP_VIEWPORT_HEIGHT;
  const longSide = Math.max(screenWidth, screenHeight);
  const shortSide = Math.max(1, Math.min(screenWidth, screenHeight));
  const scaleByWidth = longSide / PHONE_LANDSCAPE_DESKTOP_VIEWPORT_WIDTH;
  const scaleByHeight = shortSide / PHONE_LANDSCAPE_DESKTOP_VIEWPORT_HEIGHT;
  return clampNumber(Math.min(scaleByWidth, scaleByHeight, 1), 0.25, 1);
};

const getViewportMetaElement = () => {
  if (typeof document === "undefined") return null;
  let viewportMeta = document.querySelector('meta[name="viewport"]');
  if (!viewportMeta) {
    viewportMeta = document.createElement("meta");
    viewportMeta.setAttribute("name", "viewport");
    document.head.appendChild(viewportMeta);
  }
  return viewportMeta;
};

const GameRootLanding = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search || "");
  const routeState =
    location.state && typeof location.state === "object" && !Array.isArray(location.state)
      ? location.state
      : null;
  const gameIndexTab = String(queryParams.get("tab") || "")
    .trim()
    .toLowerCase();
  const gameIndexQuickView = String(
    queryParams.get("quickView") || routeState?.openMatchmakingQuickView || ""
  )
    .trim()
    .toLowerCase();
  const shouldPreserveGameIndexTabOnPhone =
    gameIndexTab === "news" || gameIndexTab === "admin";
  const shouldPreserveGameIndexQuickViewOnPhone = [
    "torneos",
    "independientes",
    "puzzles",
    "daily-puzzles",
    "buscar-partidos",
  ].includes(gameIndexQuickView);
  const shouldPreserveWaitingRoomOnPhone = Boolean(
    String(
      queryParams.get("openTournamentWaitingId") ||
        routeState?.openTournamentWaitingId ||
        ""
    ).trim()
  );
  const shouldPreservePuzzleOpenOnPhone = Boolean(
    String(
      routeState?.openPuzzleId ||
        routeState?.openPuzzleOrder ||
        routeState?.openNextPuzzleFromId ||
        routeState?.openRetryPuzzleFromId ||
        ""
    ).trim()
  );
  const shouldPreserveGameIndexRequestOnPhone =
    shouldPreserveGameIndexTabOnPhone ||
    shouldPreserveGameIndexQuickViewOnPhone ||
    shouldPreserveWaitingRoomOnPhone ||
    shouldPreservePuzzleOpenOnPhone;

  if (isPhonePortraitMobileDevice() && !shouldPreserveGameIndexRequestOnPhone) {
    return <Navigate to="/game/lobby" replace />;
  }

  const hasRouteState = (() => {
    if (String(location.search || "").trim() !== "") return true;
    if (String(location.hash || "").trim() !== "") return true;
    if (!location.state) return false;
    if (Array.isArray(location.state)) return location.state.length > 0;
    if (typeof location.state !== "object") return true;
    return Object.keys(location.state).length > 0;
  })();
  const isHomeExplicitNavigation =
    queryParams.get("from") === "home-button";

  if (isHomeExplicitNavigation || hasRouteState) return <GameIndex />;
  return <Navigate to="/game/lobby" replace />;
};

const AuthenticatedVisitRouteTracker = () => {
  const location = useLocation();
  const { user, loading } = useAuth() || {};
  const lastRouteSignatureRef = useRef("");
  const skippedInitialAuthenticatedRouteRef = useRef(false);

  useEffect(() => {
    if (loading || !user) return;

    const routeSignature = `${location.pathname || ""}${location.search || ""}${
      location.hash || ""
    }`;
    if (!routeSignature || routeSignature === lastRouteSignatureRef.current) return;

    lastRouteSignatureRef.current = routeSignature;
    if (!skippedInitialAuthenticatedRouteRef.current) {
      skippedInitialAuthenticatedRouteRef.current = true;
      return;
    }

    notifyPageVisit({ eventType: "route_change" });
  }, [loading, location.hash, location.pathname, location.search, user]);

  return null;
};

const PublicLearnScrollShell = ({ children }) => {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const htmlElement = document.documentElement;
    const bodyElement = document.body;
    htmlElement.classList.add("xima-public-learn-page");
    bodyElement.classList.add("xima-public-learn-page");

    return () => {
      htmlElement.classList.remove("xima-public-learn-page");
      bodyElement.classList.remove("xima-public-learn-page");
    };
  }, []);

  return children;
};

const PublicLearnRouter = () => (
  <PublicLearnScrollShell>
    <AuthProvider>
      <PublicRouter>
        <Routes>
          <Route path={PUBLIC_LEARN_BASE} element={<Learn publicMode />} />
          <Route path={`${PUBLIC_LEARN_BASE}/*`} element={<Learn publicMode />} />
          <Route path="*" element={<Navigate to={PUBLIC_LEARN_BASE} replace />} />
        </Routes>
      </PublicRouter>
    </AuthProvider>
  </PublicLearnScrollShell>
);

const App = () => {
  useEffect(() => {
    if (typeof document === "undefined" || typeof window === "undefined") return undefined;

    const htmlElement = document.documentElement;
    const bodyElement = document.body;
    const viewportMeta = getViewportMetaElement();
    const originalViewportContent =
      viewportMeta?.getAttribute("content") || DEFAULT_VIEWPORT_META_CONTENT;

    const syncMobilePhoneClass = () => {
      const isPhone = isPhoneMobileDevice();
      const isPhoneLandscape = isPhone && isPhoneLandscapeMobileDevice();
      htmlElement.classList.toggle("xima-mobile-phone", isPhone);
      bodyElement.classList.toggle("xima-mobile-phone", isPhone);
      htmlElement.classList.toggle("xima-mobile-phone-landscape", isPhoneLandscape);
      bodyElement.classList.toggle("xima-mobile-phone-landscape", isPhoneLandscape);

      if (!viewportMeta) return;
      if (isPhoneLandscape) {
        const scale = resolvePhoneLandscapeDesktopScale();
        viewportMeta.setAttribute(
          "content",
          `width=${PHONE_LANDSCAPE_DESKTOP_VIEWPORT_WIDTH}, initial-scale=${scale.toFixed(
            4
          )}, minimum-scale=${scale.toFixed(4)}, maximum-scale=${scale.toFixed(
            4
          )}, user-scalable=no, viewport-fit=cover`
        );
        return;
      }
      viewportMeta.setAttribute("content", originalViewportContent);
    };

    syncMobilePhoneClass();
    window.addEventListener("resize", syncMobilePhoneClass);
    window.addEventListener("orientationchange", syncMobilePhoneClass);
    window.visualViewport?.addEventListener("resize", syncMobilePhoneClass);

    return () => {
      window.removeEventListener("resize", syncMobilePhoneClass);
      window.removeEventListener("orientationchange", syncMobilePhoneClass);
      window.visualViewport?.removeEventListener("resize", syncMobilePhoneClass);
      if (viewportMeta) {
        viewportMeta.setAttribute("content", originalViewportContent);
      }
      htmlElement.classList.remove("xima-mobile-phone");
      htmlElement.classList.remove("xima-mobile-phone-landscape");
      bodyElement.classList.remove("xima-mobile-phone");
      bodyElement.classList.remove("xima-mobile-phone-landscape");
    };
  }, []);

  if (typeof window !== "undefined" && isPublicLearnPathname(window.location.pathname)) {
    return <PublicLearnRouter />;
  }

  return (
    <AuthProvider>
      <Router>
        <AuthenticatedVisitRouteTracker />
        <Routes>
          {/* Public routes: If authenticated, user will be redirected */}
          <Route
            path="/login"
            element={<PublicRoute restricted={true} component={LoginForm} />}
          />
          <Route
            path="/register"
            element={<PublicRoute restricted={true} allowGuest component={RegisterForm} />}
          />
          <Route path="/guest-launch/:action" element={<GuestLaunch />} />
          <Route path="/entrar/:kind/:id" element={<GuestLinkEntry />} />
          <Route path="/p/:id" element={<ShortLinkRedirect kind="p" />} />
          <Route path="/t/:id" element={<ShortLinkRedirect kind="t" />} />
          <Route path="/i/:id" element={<ShortLinkRedirect kind="i" />} />

          {/* Protected routes: Only accessible if authenticated */}
          {["owner", "member"].map((basePath) => (
            <Route
              key={basePath}
              path={`/${basePath}`}
              element={
                <ProtectedRoute role={OWNER_ROLES} component={OwnerLayout} />
              }
            >
              {/* Owner's child routes */}
              <Route path="dashboard" element={<OwnerDashboard />} />
              <Route path="my-tests" element={<MyTests />} />
              {/* <Route path="sales-reports" element={<SalesReports />} /> */}
              {/* <Route path="profile" element={<OwnerProfile />} /> */}
            </Route>
          ))}

          <Route
            path="/game/create-game"
            element={
              <ProtectedRoute role={["user", "admin", "superuser"]} component={CreateGame} />
            }
          />

          <Route
            path="/game/play-bot"
            element={
              <ProtectedRoute role={["user", "admin", "superuser"]} component={PlayBot} />
            }
          />

          <Route
            path="/game/play-sandbox"
            element={
              <ProtectedRoute role={["user", "admin", "superuser"]} component={PlaySandbox} />
            }
          />

          <Route
            path="/game/play-local-friends"
            element={
              <ProtectedRoute role={["user", "admin", "superuser"]} component={PlayLocalFriends} />
            }
          />

          <Route
            path="/game/design"
            element={
              <ProtectedRoute role={["user", "admin", "superuser"]} component={DesignMode} />
            }
          />

          <Route
            path="/game/play"
            element={
              <ProtectedRoute role={["user", "admin", "superuser"]} component={PlayTournaments} />
            }
          />

          <Route
            path="/game"
            element={
              <ProtectedRoute role={["user", "admin", "superuser"]} component={GameLayout} />
            }
          >
            {/* Game child routes */}
            <Route path="" element={<GameRootLanding />} />
            {/* Entry route for /game: lobby by default unless an explicit game view is requested */}
            <Route path="history" element={<History />} />
            <Route path="stats" element={<Stats />} />
            <Route path="ranking" element={<Ranking />} />
            <Route path="learn" element={<Learn />} />
            <Route path="learn/*" element={<Learn />} />
            <Route path="support" element={<Donate />} />
            <Route path="donate" element={<Navigate to="/game/support" replace />} />
            <Route path="store" element={<Store />} />
            <Route path="premium" element={<Premium />} />
            <Route path="friends" element={<Friends />} />
            <Route path="player/:playerCode" element={<FriendProfile />} />
            <Route path="spectate" element={<SpectateMatches />} />
            <Route path="settings" element={<Settings />} />
            <Route path="lobby" element={<Lobby />} />
            <Route path="championship" element={<Championship />} />
            <Route path=":id" element={<TournamentRoute />} />
          </Route>

          <Route
            path="/admin"
            element={<ProtectedRoute role="admin" component={AdminLayout} />}
          >
            {/* Admin's child route */}
            <Route path="dashboard" element={<AdminDashboard />} />
          </Route>

          {/* Public landing page for first-time and signed-out visitors */}
          <Route
            path="/"
            element={<PublicRoute restricted={true} component={LandingPage} />}
          />
          {/* Default route for undefined paths */}
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
};

export default App;
